/**
 * Locale URL utilities for locale-prefixed routing.
 * Handles parsing, building, and validating locale-prefixed URLs.
 */

import { getEnv } from "@/config/env";

export const DEFAULT_LOCALE = "sv-SE";

/** "en" is the global shop locale — no country attached. */
export const GLOBAL_LOCALE = "en";

const LOCALE_PATTERN = /^[a-z]{2}-[A-Z]{2}$/;

/**
 * Normalize a locale string to its internal casing: "nb-no" → "nb-NO", "en" → "en".
 * This is the form used for state, storage and API headers — not for URLs
 * (see toUrlLocale). Returns the input unchanged if it doesn't look like a locale.
 */
export function normalizeLocale(locale: string): string {
  if (locale.toLowerCase() === GLOBAL_LOCALE) return GLOBAL_LOCALE;
  const parts = locale.split("-");
  if (parts.length === 2) {
    return `${parts[0]!.toLowerCase()}-${parts[1]!.toUpperCase()}`;
  }
  return locale;
}

/**
 * Check if a string is a recognised locale prefix.
 * Accepts full culture codes ("sv-SE", "en-GB") and the special
 * global shop code "en". Case-insensitive: "nb-no" is treated as "nb-NO".
 */
export function isValidLocaleFormat(locale: string): boolean {
  const normalized = normalizeLocale(locale);
  return normalized === GLOBAL_LOCALE || LOCALE_PATTERN.test(normalized);
}

/**
 * Extract locale prefix from a pathname.
 * "/sv-SE/products/luna" → "sv-SE"
 * "/products/luna" → null
 * "/" → null
 */
export function extractLocaleFromPath(pathname: string): string | null {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0];
  if (!first) return null;
  if (!isValidLocaleFormat(first)) return null;
  return normalizeLocale(first);
}

/**
 * Strip locale prefix from a pathname.
 * "/sv-SE/products/luna" → "/products/luna"
 * "/sv-SE/" → "/"
 * "/products/luna" → "/products/luna"
 */
export function stripLocaleFromPath(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0];
  if (!first || !isValidLocaleFormat(first)) return pathname;
  // Remove the raw URL segment (which may differ in casing from the normalized locale)
  const stripped = pathname.replace(`/${first}`, "");
  return stripped || "/";
}

/**
 * The locale as it appears in a URL: always lower case ("nb-NO" → "nb-no").
 * Paths are case-sensitive for search engines, so the lower-case form (the one
 * the CMS, sitemaps and hreflang use) is the only URL form; nginx 301s
 * upper-case locale segments to it (GROW-391).
 */
export function toUrlLocale(locale: string): string {
  return locale.toLowerCase();
}

/**
 * Build a locale-prefixed path with the URL (lower-case) form of the locale.
 * ("en-GB", "/products/luna") → "/en-gb/products/luna"
 * ("sv-SE", "/") → "/sv-se/"
 */
export function buildLocalePath(locale: string, path: string): string {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `/${toUrlLocale(locale)}${cleanPath}`;
}

/**
 * Extract country code from a culture code.
 * "sv-SE" → "SE"
 * "en-GB" → "GB"
 * "en"    → "EN"  (global shop — maps to the "EN" country entry in the API)
 */
export function getCountryFromCulture(cultureCode: string): string {
  const parts = cultureCode.split("-");
  return (
    (parts.length > 1 ? parts[1] : parts[0])?.toUpperCase() ??
    cultureCode.toUpperCase()
  );
}

/**
 * Convert a 2-letter country code to a flag emoji.
 * "SE" → "🇸🇪"
 * "GB" → "🇬🇧"
 * "EN" → "🌐"  (global — not a real ISO country)
 */
export function getFlagEmoji(countryCode: string): string {
  if (countryCode.toUpperCase() === "EN") return "🌐";
  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

/**
 * Get the path to the SVG flag image for a country code.
 * Falls back to null when no SVG is available.
 * "SE" → "/images/flags/SE.svg"
 * "EN" → "/images/flags/EN.svg"  (global)
 */
export function getCountryFlagSrc(countryCode: string): string | null {
  const code = countryCode.toUpperCase();
  const available = ["EN", "GB", "SE", "FI", "DE", "NO", "FR", "DK"];
  return available.includes(code) ? `/images/flags/${code}.svg` : null;
}

/**
 * Map a language code to the flag that best represents it.
 * "en" → GB flag, "sv" → SE flag, "fi" → FI flag, etc.
 */
const LANGUAGE_FLAG_MAP: Record<string, string> = {
  en: "GB",
  sv: "SE",
  fi: "FI",
  de: "DE",
  no: "NO",
  nb: "NO",
  nn: "NO",
  fr: "FR",
  da: "DK",
};

export function getLanguageFlagSrc(languageCode: string): string | null {
  const country = LANGUAGE_FLAG_MAP[languageCode.toLowerCase()];
  return country ? getCountryFlagSrc(country) : null;
}

export interface LocaleInfo {
  country: string;
  currency: string;
}

/**
 * Supported locales available in the shop — only cultures that are
 * actually published in Umbraco and returned by GetValidDisplayCultures().
 * Note: en-US is installed but NOT published; en-001 is the root culture
 * and excluded from display cultures.
 */
const SUPPORTED_LOCALES: Record<string, LocaleInfo> = {
  "sv-SE": { country: "SE", currency: "SEK" },
  "sv-FI": { country: "FI", currency: "EUR" },
  "fi-FI": { country: "FI", currency: "EUR" },
  "de-DE": { country: "DE", currency: "EUR" },
  "fr-FR": { country: "FR", currency: "EUR" },
  "nb-NO": { country: "NO", currency: "NOK" },
  "da-DK": { country: "DK", currency: "DKK" },
  en: { country: "EN", currency: "EUR" },
};

/**
 * Per-site overrides of the locale → country/currency mapping.
 * wehunt.no sells exclusively in NOK, so on the wehuntno deployment the
 * global-English locale maps to Norway/NOK instead of the shared
 * Global entry (country "EN" / EUR) used by the other deployments.
 */
const SITE_LOCALE_OVERRIDES: Record<string, Record<string, LocaleInfo>> = {
  wehuntno: {
    en: { country: "NO", currency: "NOK" },
  },
};

/**
 * Per-site starting locale for a visitor with nothing stored yet.
 *
 * Without an entry a deployment falls back to DEFAULT_LOCALE ("sv-SE"), which on
 * wehunt.no meant a first-time visitor opened the Swedish market — sending
 * X-Store-Country: SE to a store that only sells to Norway until App locale sync
 * corrected them.
 */
const SITE_DEFAULT_LOCALE: Record<string, string> = {
  wehuntno: "nb-NO",
};

/** The locale a visitor to this deployment starts on before any preference exists. */
export function getSiteDefaultLocale(): string {
  return SITE_DEFAULT_LOCALE[getEnv().siteId] ?? DEFAULT_LOCALE;
}

/**
 * Returns the current site's override for a locale, or null when the site
 * uses the shared SUPPORTED_LOCALES mapping. Exposed so callers (App locale
 * sync) can force-correct a stored locale that predates the override.
 */
export function getSiteLocaleOverride(locale: string): LocaleInfo | null {
  return SITE_LOCALE_OVERRIDES[getEnv().siteId]?.[locale] ?? null;
}

/**
 * Resolve a locale code to its country and currency.
 * "sv-SE" → { country: "SE", currency: "SEK" }
 * "en"    → { country: "EN", currency: "EUR" }
 *           (on the wehuntno site: { country: "NO", currency: "NOK" })
 * Unknown → null
 */
export function resolveLocaleInfo(locale: string): LocaleInfo | null {
  return getSiteLocaleOverride(locale) ?? SUPPORTED_LOCALES[locale] ?? null;
}

/**
 * Detect the best matching locale from the browser's language preferences.
 *
 * Matching priority for each browser language:
 *  1. Exact match (e.g., "sv-SE" → "sv-SE")
 *  2. Case-normalized match (e.g., "sv-se" → "sv-SE")
 *  3. Base language match (e.g., "sv" → "sv-SE", "de" → "de-DE")
 *
 * Falls back to the global "en" locale if nothing matches.
 */
export function detectBrowserLocale(): {
  language: string;
  country: string;
  currency: string;
} {
  const browserLangs =
    typeof navigator !== "undefined" ? (navigator.languages ?? []) : [];

  const supportedKeys = Object.keys(SUPPORTED_LOCALES);

  const match = (key: string) => {
    // resolveLocaleInfo (not SUPPORTED_LOCALES directly) so per-site
    // overrides apply, e.g. "en" → NO/NOK on the wehuntno deployment.
    const info = resolveLocaleInfo(key);
    return info
      ? { language: key, country: info.country, currency: info.currency }
      : null;
  };

  for (const browserLang of browserLangs) {
    // 1. Exact match
    const exact = match(browserLang);
    if (exact) return exact;

    // 2. Case-normalized match (browser may send "sv-se" instead of "sv-SE")
    const normalized = supportedKeys.find(
      (k) => k.toLowerCase() === browserLang.toLowerCase()
    );
    if (normalized) return match(normalized)!;

    // 3. Base language match — find the first supported locale starting with this language
    const base = browserLang.split("-")[0]?.toLowerCase();
    if (!base) continue;
    const baseMatch = supportedKeys.find(
      (k) => k.toLowerCase().split("-")[0] === base && k !== GLOBAL_LOCALE
    );
    if (baseMatch) return match(baseMatch)!;
  }

  // No match — fall back to global English
  return match(GLOBAL_LOCALE)!;
}
